import { Injectable } from '@angular/core'
import { AuthenticatedCacheService } from '@services/authenticated-cache.service'
import { DateTime } from 'luxon'
import { environment } from '@environments/environment'
import { VenueCalendarDTO } from '@services/DTO/VenueCalendarDTO'
import { VenueScheduleDTO } from '@services/DTO/VenueScheduleDTO'
import { BookingStatusType } from '@app/domain/BookingStatus'
import { BookingDTO } from '@services/DTO/BookingDTO'

type BookingsCacheValue = {
    dtos: unknown[]
    schedule?: VenueScheduleDTO
    endDate?: string
    statuses?: string
}

type CacheMeta = {
    dates: string[]
}

@Injectable({ providedIn: 'root' })
export class BookingsCacheService {

    private static readonly MAX_OTHER_DATES = 3
    private static readonly CACHE_PREFIX = 'bookings'
    private static readonly CALENDAR_CACHE_PREFIX = 'booking-calendar'

    constructor(private authenticatedCacheService: AuthenticatedCacheService) { }

    store(venueId: string, date: string, dtos: unknown[]): void {
        this.authenticatedCacheService.store(this.cacheKey(venueId, date), { dtos })
        this.touchDate(venueId, date)
    }

    retrieve(venueId: string, date: string): { dtos: unknown[], cachedAt: Date } | null {
        const entry = this.authenticatedCacheService.retrieveEntry<BookingsCacheValue>(
            this.cacheKey(venueId, date)
        )
        if (!entry || !Array.isArray(entry.value.dtos) || entry.value.schedule) {
            return null
        }
        return {
            dtos: entry.value.dtos,
            cachedAt: entry.cachedAt,
        }
    }

    storeCalendar(venueId: string, startDate: string, endDate: string, dto: VenueCalendarDTO, statuses?: BookingStatusType[]): void {
        this.authenticatedCacheService.store(this.cacheKey(venueId, startDate, BookingsCacheService.CALENDAR_CACHE_PREFIX), {
            dtos: dto.bookings,
            schedule: dto.schedule,
            endDate,
            statuses: statuses?.slice().sort().join(','),
        })
        this.touchDate(venueId, startDate, BookingsCacheService.CALENDAR_CACHE_PREFIX)
    }

    retrieveCalendar(venueId: string, startDate: string, endDate: string, statuses?: BookingStatusType[]): { dto: VenueCalendarDTO, cachedAt: Date } | null {
        const entry = this.authenticatedCacheService.retrieveEntry<BookingsCacheValue>(this.cacheKey(venueId, startDate, BookingsCacheService.CALENDAR_CACHE_PREFIX))
        if (!entry || !Array.isArray(entry.value.dtos) || !entry.value.schedule || entry.value.endDate !== endDate ||
            entry.value.statuses !== statuses?.slice().sort().join(',')) {
            return null
        }
        return {
            dto: { bookings: entry.value.dtos as BookingDTO[], schedule: entry.value.schedule },
            cachedAt: entry.cachedAt,
        }
    }

    clearAll(): void {
        this.authenticatedCacheService.clearMatching(`${BookingsCacheService.CACHE_PREFIX}.`)
        this.authenticatedCacheService.clearMatching(`${BookingsCacheService.CALENDAR_CACHE_PREFIX}.`)
    }

    private touchDate(venueId: string, date: string, prefix = BookingsCacheService.CACHE_PREFIX): void {
        const meta = this.getMeta(venueId, prefix)
        const today = DateTime.now().setZone(environment.assumedVenueTimeZone).toISODate()!
        const isToday = date === today

        const otherDates = meta.dates.filter(d => d !== date && d !== today)

        if (!isToday) {
            otherDates.unshift(date)
        }

        while (otherDates.length > BookingsCacheService.MAX_OTHER_DATES) {
            const evicted = otherDates.pop()!
            this.authenticatedCacheService.remove(this.cacheKey(venueId, evicted, prefix))
        }

        const allDates = []
        if (meta.dates.includes(today) || isToday) {
            allDates.push(today)
        }
        allDates.push(...otherDates)

        this.setMeta(venueId, { dates: allDates }, prefix)
    }

    private getMeta(venueId: string, prefix: string): CacheMeta {
        const meta = this.authenticatedCacheService.retrieve<CacheMeta>(this.metaKey(venueId, prefix))
        return Array.isArray(meta?.dates) && meta.dates.every(date => typeof date === 'string') ? meta : { dates: [] }
    }

    private setMeta(venueId: string, meta: CacheMeta, prefix: string): void {
        this.authenticatedCacheService.store(this.metaKey(venueId, prefix), meta)
    }

    private cacheKey(venueId: string, date: string, prefix = BookingsCacheService.CACHE_PREFIX): string {
        return `${prefix}.${venueId}.${date}`
    }

    private metaKey(venueId: string, prefix: string): string {
        return `${prefix}.${venueId}.meta`
    }
}
